import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            {/* Playful broken egg icon */}
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-b from-amber-100 to-amber-200 dark:from-amber-200 dark:to-amber-300 rounded-full transform rotate-12 shadow-lg" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-10 bg-amber-50 dark:bg-amber-100 rounded-t-full border-b-4 border-amber-200 dark:border-amber-300" style={{ clipPath: 'polygon(0 100%, 15% 40%, 30% 80%, 50% 30%, 70% 70%, 85% 50%, 100% 100%)' }} />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-8 h-8 bg-amber-400 dark:bg-amber-500 rounded-full shadow-inner" />
              <AlertTriangle className="absolute bottom-1 right-1 w-6 h-6 text-destructive animate-pulse" />
            </div>

            <h1 className="text-xl font-semibold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              We've refreshed the kitchen. Let's try that again.
            </p>

            <Button
              onClick={this.handleReload}
              className="gradient-primary text-primary-foreground"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload App
            </Button>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left text-xs text-muted-foreground bg-secondary/50 p-3 rounded-lg">
                <summary className="cursor-pointer font-medium">Error details</summary>
                <pre className="mt-2 overflow-auto whitespace-pre-wrap">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}